'use client';

import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { GitHubMark } from '@/assets/GitHubMark';

interface InstallPrBotModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Git org the app installs into. Shown so the user knows which GitHub
   * account they are about to land on, which matters once a workspace is
   * connected to more than one.
   */
  orgName?: string;
  /**
   * Repo the user clicked "Grant access" on, when the dialog was opened from a
   * row rather than the header button. GitHub's install page accepts no
   * per-repo preselect (only `suggested_target_id` for the account), so naming
   * the repo here is the only way to carry that intent across the redirect.
   */
  repoName?: string;
  /**
   * Git orgs this workspace is connected to. When there is more than one, the
   * dialog asks which before leaving: GitHub's install page is per-account, so
   * without a choice the user lands on whichever org happens to be first and
   * has to navigate out of it themselves.
   */
  orgChoices?: string[];
  /** Navigates to the GitHub App install page, into `org` when one was picked. */
  onProceed: (org?: string) => void;
}

export default function InstallPrBotModal({
  isOpen,
  onClose,
  orgName,
  repoName,
  orgChoices,
  onProceed,
}: InstallPrBotModalProps) {
  const choices = orgChoices ?? [];
  const mustChoose = choices.length > 1;
  const [chosenOrg, setChosenOrg] = useState<string | null>(null);

  // Reset between openings, so a previous pick does not silently apply to the
  // next install, which could be for a different repo entirely.
  useEffect(() => {
    if (isOpen) setChosenOrg(null);
  }, [isOpen]);

  const targetOrg = mustChoose ? chosenOrg : (choices[0] ?? orgName);

  const steps = [
    targetOrg
      ? `GitHub opens the Entelligence app install page for ${targetOrg}.`
      : 'GitHub opens the Entelligence app install page.',
    repoName
      ? `Select ${repoName} there, along with any other repositories you want reviewed.`
      : 'Choose the repositories you want the PR bot to review.',
    'You land back here and the list refreshes on its own.',
  ];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Install the PR bot on GitHub</DialogTitle>
          <DialogDescription>
            Repository access is granted on GitHub, not here. We will take you
            there and bring you straight back.
          </DialogDescription>
        </DialogHeader>

        {mustChoose ? (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-ds-text-darker">
              Which organization?
            </span>
            <div className="flex flex-col gap-1.5">
              {choices.map((org) => (
                <button
                  key={org}
                  type="button"
                  onClick={() => setChosenOrg(org)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                    chosenOrg === org
                      ? 'border-ds-brand-subtle bg-ds-brand-lightest text-ds-text-darker'
                      : 'border-ds-border-subtler text-ds-text-default hover:bg-ds-gray-subtlest'
                  }`}
                >
                  <GitHubMark className="h-4 w-4 shrink-0 text-ds-text-subtle" />
                  {org}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <ol className="flex flex-col gap-3">
          {steps.map((step, index) => (
            <li key={step} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ds-brand-subtlest text-xs font-semibold text-ds-brand-subtle">
                {index + 1}
              </span>
              <span className="text-sm text-ds-text-default">{step}</span>
            </li>
          ))}
        </ol>

        <div className="flex items-start gap-3 rounded-lg border border-ds-border-subtlest bg-ds-gray-subtlest p-3">
          <GitHubMark className="mt-0.5 h-4 w-4 shrink-0 text-ds-text-subtle" />
          <p className="text-xs text-ds-text-subtle">
            Only repositories you pick on GitHub are reviewed. You can change
            the selection at any time from the same page.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={() => onProceed(targetOrg ?? undefined)}
            disabled={mustChoose && !chosenOrg}
          >
            Continue to GitHub
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
